import React, { useState } from 'react';
import { AdminProposalSummary } from '../lib/adminTypes';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { X, FileBox, CheckCircle2, AlertTriangle, Download, Loader2, Sparkles, ShieldCheck, Lightbulb, ChevronDown } from 'lucide-react';
interface ExportPacketModalProps {
  proposal: AdminProposalSummary;
  open: boolean;
  onClose: () => void;
}
export function ExportPacketModal({
  proposal,
  open,
  onClose
}: ExportPacketModalProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [exportComplete, setExportComplete] = useState(false);
  const [syncDropdownOpen, setSyncDropdownOpen] = useState(false);
  if (!open) return null;
  const handleExport = () => {
    setIsExporting(true);
    setTimeout(() => {
      setIsExporting(false);
      setExportComplete(true);
    }, 2000);
  };
  const handleClose = () => {
    setExportComplete(false);
    setSyncDropdownOpen(false);
    onClose();
  };
  const allArtifactsReady = proposal.artifactsCompleted === proposal.artifactsTotal;
  const allCriteriaSatisfied = proposal.criteriaSatisfied === proposal.criteriaTotal;
  const aiCheckPassed = proposal.aiCheckStatus === 'all-passed';
  const artifactPercent = Math.round(proposal.artifactsCompleted / proposal.artifactsTotal * 100);
  const isFullyReady = allArtifactsReady && allCriteriaSatisfied && aiCheckPassed;
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={handleClose} data-id="element-194">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-195">
        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-5 pb-4 border-b border-border" data-id="element-196">
          <div className="min-w-0 flex-1 pr-4" data-id="element-197">
            <h2 className="text-base font-semibold text-foreground leading-snug" data-id="element-198">
              Export Packet
            </h2>
            <p className="text-xs text-muted-foreground mt-1 truncate" data-id="element-199">
              {proposal.title}
            </p>
          </div>
          <button onClick={handleClose} className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-muted -mt-1 -mr-1" data-id="element-200">
            <X className="h-4 w-4" data-id="element-201" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5" data-id="element-202">
          {exportComplete ? <div className="flex flex-col items-center justify-center py-6 text-center space-y-3" data-id="element-203">
              <div className="h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center" data-id="element-204">
                <CheckCircle2 className="h-7 w-7 text-emerald-600" data-id="element-205" />
              </div>
              <div data-id="element-206">
                <h3 className="text-base font-semibold text-foreground" data-id="element-207">
                  Export Complete
                </h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-[280px] mx-auto" data-id="element-208">
                  The submission packet has been generated and is ready for
                  download.
                </p>
              </div>
              <Button onClick={handleClose} className="mt-2 bg-indigo-600 hover:bg-indigo-700" size="sm" data-id="element-209">
                Done
              </Button>
            </div> : <div className="space-y-4" data-id="element-210">
              {/* Readiness Checklist */}
              <div className="space-y-2" data-id="element-211">
                <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider" data-id="element-212">
                  Readiness Checklist
                </p>

                {/* Artifacts */}
                <div className="flex items-center gap-3 p-3 rounded-lg border border-border" data-id="element-213">
                  <div className={`p-1.5 rounded-md ${allArtifactsReady ? 'bg-emerald-50' : 'bg-amber-50'}`} data-id="element-214">
                    <FileBox className={`h-4 w-4 ${allArtifactsReady ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-215" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-216">
                    <div className="flex items-center justify-between mb-1" data-id="element-217">
                      <p className="text-sm font-medium text-foreground" data-id="element-218">
                        Documents
                      </p>
                      <span className="text-xs text-muted-foreground tabular-nums" data-id="element-219">
                        {proposal.artifactsCompleted}/{proposal.artifactsTotal}
                      </span>
                    </div>
                    <Progress value={artifactPercent} className="h-1.5 bg-slate-100" data-id="element-220" />
                  </div>
                  {allArtifactsReady ? <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" data-id="element-221" /> : <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" data-id="element-222" />}
                </div>

                {/* Criteria */}
                <div className="flex items-center gap-3 p-3 rounded-lg border border-border" data-id="element-223">
                  <div className={`p-1.5 rounded-md ${allCriteriaSatisfied ? 'bg-emerald-50' : 'bg-amber-50'}`} data-id="element-224">
                    <Lightbulb className={`h-4 w-4 ${allCriteriaSatisfied ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-225" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-226">
                    <div className="flex items-center gap-1.5" data-id="element-227">
                      <p className="text-sm font-medium text-foreground" data-id="element-228">
                        Criteria
                      </p>
                      <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-[9px] px-1 py-0 h-4 gap-0.5" data-id="element-229">
                        <Sparkles className="h-2 w-2" data-id="element-230" />
                        AI
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground" data-id="element-231">
                      {proposal.criteriaSatisfied} of {proposal.criteriaTotal}{' '}
                      criteria met
                    </p>
                  </div>
                  {allCriteriaSatisfied ? <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" data-id="element-232" /> : <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" data-id="element-233" />}
                </div>

                {/* Compliance */}
                <div className="flex items-center gap-3 p-3 rounded-lg border border-border" data-id="element-234">
                  <div className={`p-1.5 rounded-md ${aiCheckPassed ? 'bg-emerald-50' : 'bg-amber-50'}`} data-id="element-235">
                    <FileBox className={`h-4 w-4 ${aiCheckPassed ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-236" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-237">
                    <div className="flex items-center gap-1.5" data-id="element-238">
                      <p className="text-sm font-medium text-foreground" data-id="element-239">
                        Compliance
                      </p>
                      <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-[9px] px-1 py-0 h-4 gap-0.5" data-id="element-240">
                        <Sparkles className="h-2 w-2" data-id="element-241" />
                        AI
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground" data-id="element-242">
                      {aiCheckPassed ? 'All checks passed' : proposal.aiCheckStatus === 'not-checked' ? 'Not yet checked' : `${proposal.aiIssueCount} issue${proposal.aiIssueCount !== 1 ? 's' : ''} detected`}
                    </p>
                  </div>
                  {aiCheckPassed ? <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" data-id="element-243" /> : <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-[10px] px-1.5 h-5 flex-shrink-0" data-id="element-244">
                      {proposal.aiIssueCount || '—'}
                    </Badge>}
                </div>
              </div>

              {/* Warning */}
              {!isFullyReady && <div className="p-3 rounded-lg bg-amber-50/80 border border-amber-200/60 flex gap-2.5 items-start" data-id="element-245">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" data-id="element-246" />
                  <p className="text-xs text-amber-800 leading-relaxed" data-id="element-247">
                    This proposal has incomplete items. You can still export,
                    but the packet may not be submission-ready.
                  </p>
                </div>}

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-3 border-t border-border" data-id="element-248">
                <Button variant="outline" size="sm" onClick={handleClose} data-id="element-249">
                  Cancel
                </Button>
                <div className="relative" data-id="element-250">
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setSyncDropdownOpen(!syncDropdownOpen)} data-id="element-251">
                    Sync with eRA
                    <ChevronDown className="h-3 w-3" data-id="element-252" />
                  </Button>
                  {syncDropdownOpen && <div className="absolute bottom-full mb-1 right-0 w-40 bg-white rounded-md border border-border shadow-lg py-1 z-10" data-id="element-253">
                      {[{
                  name: 'Cayuse',
                  logo: 'https://cdn.magicpatterns.com/uploads/x7JxfFNjFw9RWejUxPtCHY/cayuse.png'
                }, {
                  name: 'Kuali',
                  logo: 'https://cdn.magicpatterns.com/uploads/wby4eAf3GHSSpgp6Reqw48/kuali.png'
                }, {
                  name: 'InfoEd',
                  logo: 'https://cdn.magicpatterns.com/uploads/2LU66FwVpptuGoGqCZZ6qX/infoed.jpg'
                }].map(system => <button key={system.name} className="w-full text-left px-3 py-1.5 text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-2" onClick={() => {
                  setSyncDropdownOpen(false);
                }} data-id="element-254">
                          <img src={system.logo} alt={system.name} className="h-4 w-4 object-contain flex-shrink-0 rounded-sm" data-id="element-255" />
                          {system.name}
                        </button>)}
                    </div>}
                </div>
                <Button onClick={handleExport} disabled={isExporting} size="sm" className="bg-indigo-600 hover:bg-indigo-700 gap-1.5" data-id="element-256">
                  {isExporting ? <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" data-id="element-257" />
                      Generating...
                    </> : <>
                      <Download className="h-3.5 w-3.5" data-id="element-258" />
                      Export Packet
                    </>}
                </Button>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}